import { api } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Receipt } from "lucide-react";
import { useEffect, useState } from "react";
import type { CategoryResponse, LexofficeSettings } from "./settings-types";
import { CardSkeleton, SaveButton } from "./settings-ui";

// ============================================================
// LexOffice — Belegtypen & Filter-Konfiguration
// ============================================================

export function LexofficeSettingsCard() {
	const queryClient = useQueryClient();
	const query = useQuery({
		queryKey: ["settings", "lexoffice"],
		queryFn: () =>
			api.get<CategoryResponse<LexofficeSettings>>(
				"/settings/category/lexoffice",
			),
	});

	const [voucherTypes, setVoucherTypes] = useState("");
	const [excludeContacts, setExcludeContacts] = useState("");
	const [includeSubjects, setIncludeSubjects] = useState("");
	const [excludeSubjects, setExcludeSubjects] = useState("");

	useEffect(() => {
		if (query.data) {
			const d = query.data.data;
			setVoucherTypes(d.voucherTypes.join(", "));
			setExcludeContacts(d.excludeContacts.join(", "));
			setIncludeSubjects(d.includeSubjects.join(", "));
			setExcludeSubjects(d.excludeSubjects.join(", "));
		}
	}, [query.data]);

	const mutation = useMutation({
		mutationFn: (data: Partial<LexofficeSettings>) =>
			api.put("/settings/category/lexoffice", data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["settings", "lexoffice"] });
		},
	});

	function handleSave() {
		mutation.mutate({
			voucherTypes: voucherTypes
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			excludeContacts: excludeContacts
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			includeSubjects: includeSubjects
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			excludeSubjects: excludeSubjects
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
		});
	}

	if (query.isLoading) return <CardSkeleton />;

	return (
		<div className="space-y-3">
			<h2 className="font-medium text-stone-700 flex items-center gap-2">
				<Receipt size={16} className="text-green-600" />
				LexOffice — Belegtypen & Filter
			</h2>
			<div className="card space-y-4">
				<div>
					<label
						htmlFor="voucher-types"
						className="text-xs font-medium text-stone-500 block mb-1"
					>
						Belegtypen für Sync (kommasepariert)
					</label>
					<input
						id="voucher-types"
						value={voucherTypes}
						onChange={(e) => setVoucherTypes(e.target.value)}
						className="input text-sm"
						placeholder="invoice, quotation, orderconfirmation"
					/>
					<p className="text-xs text-stone-400 mt-1">
						Nur Ausgangsbelege: invoice, quotation, orderconfirmation
					</p>
				</div>
				<div>
					<label
						htmlFor="exclude-contacts"
						className="text-xs font-medium text-stone-500 block mb-1"
					>
						Kontakte ausschließen (kommasepariert)
					</label>
					<input
						id="exclude-contacts"
						value={excludeContacts}
						onChange={(e) => setExcludeContacts(e.target.value)}
						className="input text-sm"
						placeholder="Amazon, Hetzner, Google Cloud"
					/>
					<p className="text-xs text-stone-400 mt-1">
						Belege von diesen Kontakten werden beim Sync ignoriert
					</p>
				</div>
				<div>
					<label
						htmlFor="include-subjects"
						className="text-xs font-medium text-stone-500 block mb-1"
					>
						Betreff muss enthalten (kommasepariert)
					</label>
					<input
						id="include-subjects"
						value={includeSubjects}
						onChange={(e) => setIncludeSubjects(e.target.value)}
						className="input text-sm"
						placeholder="as if Spaces, Podcast Studio"
					/>
					<p className="text-xs text-stone-400 mt-1">
						Nur Belege mit passendem Betreff werden synchronisiert (leer = alle)
					</p>
				</div>
				<div>
					<label
						htmlFor="exclude-subjects"
						className="text-xs font-medium text-stone-500 block mb-1"
					>
						Betreff ausschließen (kommasepariert)
					</label>
					<input
						id="exclude-subjects"
						value={excludeSubjects}
						onChange={(e) => setExcludeSubjects(e.target.value)}
						className="input text-sm"
						placeholder="Schwarzes Gold"
					/>
					<p className="text-xs text-stone-400 mt-1">
						Belege mit diesem Betreff werden beim Sync ignoriert
					</p>
				</div>
				<SaveButton
					isPending={mutation.isPending}
					isSuccess={mutation.isSuccess}
					onClick={handleSave}
				/>
			</div>
		</div>
	);
}
